import { cloneElement, useMemo } from 'react';

import { ThemeProvider } from '@change/ds-core-ui/next';

import { FormError } from '@change-corgi/design-system-legacy/components/forms';
import { Flex } from '@change-corgi/design-system-legacy/layout';

import { Alert } from '../../../components/Alert';
import { useStripeForm } from '../v2/hooks/useStripeForm';

import { SingleForm } from './components/SingleForm';
import { SplitForm } from './components/SplitForm';
import type { StripeCreditCardFormPropsWithElements } from './types';

export function StripeCreditCardLayout({
	submitButton,
	children,
	labelWeight,
	displayErrorInAlert,
	...rest
}: StripeCreditCardFormPropsWithElements): JSX.Element {
	const {
		data: { errorMessage, validationErrors, errorType },
		actions: { handleSubmit, validateStripeElementOnChange, setValidationErrors },
	} = useStripeForm(rest);

	const { errorTitle, errorDescription } = useMemo(() => {
		if (errorType === 'api_connection_error') {
			return {
				errorTitle: 'Connection Error',
				errorDescription: errorMessage || 'Please check the details or use another card.',
			};
		}
		return {
			errorTitle: 'This card was declined',
			errorDescription: 'Please check the details or use another card.',
		};
	}, [errorType, errorMessage]);

	return (
		<form onSubmit={handleSubmit}>
			<Flex sx={{ flexDirection: 'column', gap: 16 }}>
				{children}
				{rest.layout === 'single' ? (
					<SingleForm
						{...rest}
						labelWeight={labelWeight}
						validationErrors={validationErrors}
						validateStripeElementOnChange={validateStripeElementOnChange}
						setValidationErrors={setValidationErrors}
					/>
				) : (
					<SplitForm
						{...rest}
						validationErrors={validationErrors}
						validateStripeElementOnChange={validateStripeElementOnChange}
					/>
				)}
				{displayErrorInAlert && errorMessage && (
					<ThemeProvider>
						<Alert title={errorTitle} description={errorDescription} />
					</ThemeProvider>
				)}
				<Flex sx={{ justifyContent: 'center' }}>
					{cloneElement(submitButton, {
						disabled: submitButton.props.disabled,
						loading: submitButton.props.loading,
					})}
				</Flex>
				{!displayErrorInAlert && errorMessage && (
					<FormError data-qa="stripe-form-error-message">{errorMessage}</FormError>
				)}
			</Flex>
		</form>
	);
}
